import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { SalesContractSalesPartsDataGridApi } from "src/api/generated/erp/sales/pricing/api/salesContractSalesPartsDataGridApi.ts";
import { genericNullableValue } from "src/utils/genericNullableValue.ts";
import { moneyColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { SalesContractSalesPartCostView } from "src/api/generated/erp/db/types/tables/salesContractSalesPartCostView.ts";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid.tsx";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import { AavoSelectField } from "../../../../../common/inputFields/AavoSelectField.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { faLink } from "@fortawesome/pro-regular-svg-icons";
import { SalesContractSalesPartPriceForm } from "src/components/views/erp/sales/pricing/salesContracts/SalesContractSalesPartPriceForm.tsx";
import { openFormOnDialog } from "src/components/common/dialogs/formDialog/openFormOnDialog.ts";
import { AddSalesPartsToSalesContractDataGrid } from "src/components/views/erp/sales/pricing/salesContracts/AddSalesPartsToSalesContractDataGrid.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import {
	calculateSalesMarginPercent,
	formatLabelWithCurrencyCode,
	renderMoneyCellWithCurrency,
	renderMoneyCellWithRowCurrency,
} from "src/components/views/erp/sales/salesUtils.ts";
import { formatNumber } from "src/utils/numberUtils.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface SalesContractSalesPartsDataGridProps {
	salesContractId: number;
	contractCurrencyCode: string;
}

export const SalesContractSalesPartsDataGrid = ({
	salesContractId,
	contractCurrencyCode,
}: SalesContractSalesPartsDataGridProps) => {
	const { openDialog } = useGenericDialog();
	return (
		<ClientSideDataGridModel
			gridId={"F14FB46DAC08A7AB"}
			fetchData={(params) =>
				SalesContractSalesPartsDataGridApi.getSalesContractSalesParts({
					salesContractId: salesContractId,
					...params,
				})
			}
			getRows={(data) => data.salesParts}
			getRowId={(row) => row.salesContractSalesPartId}
			initialParams={{
				searchQuery: "",
				siteId: genericNullableValue<number>(null),
			}}
			render={({ data, dataGridProps, refreshData }) => {
				return (
					<FormCrudDataGrid<SalesContractSalesPartCostView>
						columns={[
							textColumn({
								field: "siteName",
								headerName: i18n.t("site"),
							}),
							textColumn({
								field: "salesPartNo",
								headerName: i18n.t("sales_part_no"),
							}),
							textColumn({
								field: "partDescription_1",
								headerName: i18n.t("description_1"),
								width: 150,
							}),
							textColumn({
								field: "partDescription_2",
								headerName: i18n.t("description_2"),
								width: 150,
							}),
							moneyColumn({
								field: "price",
								headerName: formatLabelWithCurrencyCode(
									i18n.t("price_in_sales_currency"),
									contractCurrencyCode,
								),
								renderCell: renderMoneyCellWithCurrency(contractCurrencyCode),
							}),
							moneyColumn({
								field: "costInDomesticCurrencyAsPriceUnit",
								headerName: i18n.t("cost_in_domestic_currency"),
								width: 160,
								renderCell: renderMoneyCellWithRowCurrency((row) => row.domesticCurrencyCode),
							}),
							moneyColumn({
								field: "costInContractCurrencyAsPriceUnit",
								headerName: i18n.t("cost_in_sales_currency"),
								width: 170,
								renderCell: renderMoneyCellWithRowCurrency((row) => row.currencyCode),
							}),
							textColumn({
								field: "salesPriceUnit",
								headerName: i18n.t("price_unit"),
							}),
							textColumn({
								field: "profitMargin" as any,
								headerName: i18n.t("profit_margin"),
								valueGetter: (_, row) => {
									if (row.costInContractCurrencyAsPriceUnit == null) return "";
									const value = formatNumber(
										calculateSalesMarginPercent({
											cost: row.costInContractCurrencyAsPriceUnit,
											salesPrice: row.price,
										}),
									);
									return `${value} %`;
								},
							}),
						]}
						actionBarComponents={
							<>
								<AavoTextField
									label={i18n.t("search")}
									onSubmit={async (input) => {
										await refreshData({ searchQuery: input });
									}}
								/>
								<AavoSelectField
									label={i18n.t("site")}
									options={data.siteOptions}
									getOptionKey={(o) => o.siteId}
									getOptionLabel={(o) => o.siteName}
									onChange={async (value) => {
										await refreshData({ siteId: value });
									}}
									minWidth={150}
								/>
								<AsyncButton
									icon={faLink}
									label={i18n.t("connect_parts")}
									variant={"outlined"}
									onClick={() => {
										openFormOnDialog({
											openDialog,
											component: AddSalesPartsToSalesContractDataGrid,
											title: i18n.t("connect_parts"),
											size: "lg",
											agentContext: {
												key: defineAgentUiContextKey(
													"erp.sales.pricing.addSalesPartsToSalesContract.dialog",
												),
												title: i18n.t("connect_parts"),
												pathSegments: [i18n.t("connect_parts")],
												data: {
													salesContractId,
													contractCurrencyCode,
												},
											},
											props: (closeDialog) => ({
												salesContractId: salesContractId,
												contractCurrencyCode,
												onSubmit: async () => {
													await refreshData();
													await closeDialog();
												},
											}),
										});
									}}
								/>
							</>
						}
						form={{
							addRowEnabled: false,
							editEnabled: true,
							dialogSize: "sm",
							dialogTitle: i18n.t("edit_price"),
							agentContext: (row) => ({
								key: defineAgentUiContextKey(
									"erp.sales.pricing.salesContractSalesPartPrice.formDialog",
								),
								data: {
									salesContractId,
									salesContractSalesPartId: row?.salesContractSalesPartId ?? null,
									salesPartId: row?.salesPartId ?? null,
								},
								pathSegments:
									row?.salesContractSalesPartId ?
										[`${i18n.t("sales_part")} ${row.salesPartId}`]
									:	[i18n.t("edit_price")],
							}),
							component: ({ row, onCompleted }) => {
								if (row == null) return null;

								return (
									<SalesContractSalesPartPriceForm
										salesContractSalesPartId={row.salesContractSalesPartId}
										price={row.price}
										currencyCode={row.currencyCode}
										onCompleted={onCompleted}
									/>
								);
							},
						}}
						remove={{
							type: "enabled",
							action: async ({ items }) => {
								if (items?.[0] == null) return;
								await SalesContractSalesPartsDataGridApi.delete_({
									salesContractSalesPartId: items[0].salesContractSalesPartId,
								});
							},
						}}
						agentContext={{
							key: defineAgentUiContextKey("erp.sales.pricing.salesContractSalesParts.grid"),
							data: {
								salesContractId,
								contractCurrencyCode,
							},
							selectedRowPathSegment: (row) => `${i18n.t("sales_part")} ${row.salesPartNo}`,
							rowFields: [
								"salesContractSalesPartId",
								"salesContractId",
								"salesPartId",
								"salesPartNo",
								"partId",
							],
						}}
						{...dataGridProps}
					/>
				);
			}}
		/>
	);
};
